import React, { useCallback, useContext, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Box, Button, Typography, Breadcrumbs } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import parse from 'html-react-parser';
import { useQueryClient } from 'react-query';

import { translate as t } from 'hooks/useTranslations';
import GridSection from 'components/_layouts/GridSection';
import useArchivedManualVersions from 'views/App/Portal/Policy/ArchivedManuals/_data_sources/useArchivedManualVersions';
import { ManualVersion } from 'views/App/Portal/Policy/_types/manuals/draft';
import { ArchivedManualsContext } from '../context';
import datetimeHelper from 'helpers/datetimeHelper';
import useArchivedManualVersion from 'views/App/Portal/Policy/ArchivedManuals/_data_sources/useArchivedManualVersion';
import CommonLayout from 'views/App/Portal/_layouts/CommonLayout';

const ArchivedManualsToC: React.FC = () => {
  const queryClient = useQueryClient();
  const { id } = useParams();
  const { orgId } = useContext(ArchivedManualsContext);
  const manualQuery = useArchivedManualVersions(orgId, id || '');
  const [retrievingIds, setRetrievingIds] = React.useState<string[]>([]);
  const { retrieve: retrieveManual } = useArchivedManualVersion();

  const handleRetrieveClick = useCallback(
    (manual: ManualVersion) => {
      setRetrievingIds([...retrievingIds, manual.version.toString()]);
      retrieveManual(manual.id, manual.version.toString(), () => {
        enqueueSnackbar(
          parse(
            t(`policy.legacyArchivedManuals.retrievalConfirmation`, '', {
              title: manual.name,
              versionDate: datetimeHelper.formatDate(manual.createdAt),
            })
          ),
          {
            variant: 'success',
            autoHideDuration: 6000,
          }
        );
        window.setTimeout(() => {
          queryClient.invalidateQueries('notifications_unread_count');
          queryClient.invalidateQueries('notifications');
        }, 15000);
      });
    },
    [queryClient, retrieveManual, retrievingIds, t]
  );

  const columns = {
    name: {
      header: t('policy.manuals.properties.name'),
      flex: 2,
      renderer: (params: any) => (
        <Typography
          variant='h6'
          sx={{ fontWeight: 'semibold' }}
        >
          {params.value}
        </Typography>
      ),
    },
    version: {
      header: t('policy.manuals.properties.version'),
      flex: 1,
    },
    createdAt: {
      header: t('policy.manuals.properties.archivedAt'),
      flex: 1,
      renderer: (params: any) => datetimeHelper.formatDate(params.value),
    },
    actions: {
      header: '',
      flex: 1,
      renderer: (params: any) => (
        <Button
          size='small'
          variant='outlined'
          disabled={retrievingIds.includes(params.row.version.toString())}
          onClick={() => handleRetrieveClick(params.row)}
        >
          {retrievingIds.includes(params.row.version.toString())
            ? t('policy.legacyArchivedManuals.retrievingArchive')
            : t('policy.legacyArchivedManuals.retrieveArchive')}
        </Button>
      ),
    },
  };

  const versions = useMemo(
    () => (manualQuery.data && manualQuery.data.pages ? manualQuery.data.pages.flatMap((x) => x.content) : []),
    [manualQuery.data]
  );

  return (
    <CommonLayout
      headerProps={{
        title: t('policy.archivedManuals.versionsTitle', '', { name: versions[0]?.name || '' }),
        breadcrumbItems: [
          <Link
            key='archived-manuals'
            to='/admin/policy/archived-manuals'
            style={{ textDecoration: 'none', color: 'inherit' }}
          >
            {t('policy.archivedManuals.title')}
          </Link>,
          <Typography
            key='manual-name'
            color='text.primary'
          >
            {versions[0]?.name || ''}
          </Typography>,
        ],
      }}
    >
      <Box sx={{ display: 'grid', gridTemplateRows: '1fr', gap: 4, minHeight: 0 }}>
        <GridSection
          columns={columns}
          data={versions}
          query={manualQuery}
          gridProps={{
            getRowId: (row: any) => [row.id, row.version].join('-'),
          }}
        />
      </Box>
    </CommonLayout>
  );
};

export default ArchivedManualsToC;
